/**
 * [F-11] ย่อภาพที่เครื่อง: ด้านยาวสุด ≤ 1568px · JPEG 0.85 · แล้ว `image_sha256` ของ *ไบต์ที่จะส่ง* (SHA-256 ของภาพ ไม่ใช่ ref_hash)
 * ทำงานบน canvas ของเบราว์เซอร์ — ไม่มี sharp ที่นี่ · ไม่มีการคำนวณ hash ของเลขอ้างอิงในแอป (กฎเหล็กข้อ 6)
 */
export const MAX_SIDE = 1568;
export const JPEG_QUALITY = 0.85;

export interface PreparedImage {
  blob: Blob;
  sha256: string;
  width: number;
  height: number;
  /** พิกเซลของภาพที่ย่อแล้ว — ใช้ถอด QR จากภาพนิ่งด้วย slip-qr โดยไม่ต้องอ่านไฟล์ซ้ำ */
  imageData: ImageData;
}

/** ขนาดหลังย่อ — ไม่ขยายภาพเล็ก */
export function fitSize(w: number, h: number, max = MAX_SIDE): { width: number; height: number } {
  const s = Math.min(1, max / Math.max(w, h));
  return { width: Math.max(1, Math.round(w * s)), height: Math.max(1, Math.round(h * s)) };
}

export function sha256Hex(bytes: ArrayBuffer): Promise<string> {
  return crypto.subtle.digest("SHA-256", bytes).then((d) => Array.from(new Uint8Array(d), (b) => b.toString(16).padStart(2, "0")).join(""));
}

/** วาด source (video เฟรมสด / bitmap จากคลังภาพ) ลง canvas ขนาดที่ย่อแล้ว → JPEG + sha256 + ImageData */
export async function prepareImage(source: CanvasImageSource, srcW: number, srcH: number): Promise<PreparedImage> {
  const { width, height } = fitSize(srcW, srcH);
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d", { willReadFrequently: true })!;
  ctx.drawImage(source, 0, 0, width, height);
  const imageData = ctx.getImageData(0, 0, width, height);
  const blob = await new Promise<Blob>((resolve, reject) =>
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("toBlob ล้มเหลว"))), "image/jpeg", JPEG_QUALITY),
  );
  const sha256 = await sha256Hex(await blob.arrayBuffer());
  return { blob, sha256, width, height, imageData };
}

/** เฟรมสดย่อสำหรับถอด QR ระหว่างส่อง — เล็กพอให้ jsQR ตอบใน ~50ms · ไม่ใช่ภาพที่ส่ง */
export function frameImageData(video: HTMLVideoElement, canvas: HTMLCanvasElement, max = 640): ImageData | null {
  if (video.videoWidth === 0) return null;
  const { width, height } = fitSize(video.videoWidth, video.videoHeight, max);
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d", { willReadFrequently: true })!;
  ctx.drawImage(video, 0, 0, width, height);
  return ctx.getImageData(0, 0, width, height);
}
